import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';

import { gray100, primaryButtonBackgroundColor, red50 } from '../../../constants/colors';
import { EMAIL_INPUT_PLACEHOLDER } from '../../../constants/placeholders';
import { userInviteMailSchema } from '../../../validation/user';
import type { UserInviteMailType } from '../../../types/user';

import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import { formLabelWrapper } from '../../../constants/location';
import SideBar from '../../uiParts/menu/SideBar';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../api/firebase/config';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import CustomRadioButton from '../../uiParts/form/CustomRadioButton';
import MetaTitle from '../../utils/MetaTitle';
import { isNotCompanyMember } from '../../../utils/permission';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';

const styles = {
  formLabelWrapper,
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 960px)': {
      width: '-webkit-fill-available',
    },
  },
  label: {
    fontSize: '14px',
  },
  radioLabel: {
    fontSize: '12px',
  },
  required: {
    color: red50,
    fontSize: '14px',
    marginLeft: '6px',
  },
  formCard: {
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #E0E0E0',
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 960px)': {
      width: 'calc(100% - 32px)',
      maxWidth: '480px',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
    },
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

const StaffInvite: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user, company, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const [loading, setLoading] = useState<boolean>(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserInviteMailType>({
    resolver: yupResolver(userInviteMailSchema),
    defaultValues: {
      email: '',
      role: false,
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return watch('email') === undefined || '' || Boolean(errors.email);
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');
      // ラジオボタンを操作すると値が文字列（'true' / 'false'）になるため、文字列も判定する
      const roleValue: boolean | string = watch('role');
      const isAdmin = typeof roleValue === 'boolean' ? roleValue : roleValue === 'true';
      const role = isAdmin ? 'admin' : 'member';

      const sendStaffInvitation = httpsCallable<
        { email: string; role: string; companyId?: string | null },
        { success: boolean; message: string }
      >(functions, 'sendStaffInvitation');
      const result = await sendStaffInvitation({
        email,
        role,
        companyId: isProxyMode ? effectiveCompanyId : undefined,
      });

      if (result.data.success) {
        navigate('complete');
      } else {
        setErrorText('メール送信に失敗しました。');
        setOpen(true);
      }
    } catch (error: unknown) {
      let errorMessage = 'メール送信中にエラーが発生しました。';

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        if (firebaseError.code === 'functions/already-exists') {
          errorMessage = 'このメールアドレスは既に登録されています。';
        } else if (firebaseError.code === 'functions/resource-exhausted') {
          errorMessage = '担当者数が上限に達しています。';
        } else if (firebaseError.code === 'functions/invalid-argument') {
          errorMessage = 'メールアドレスまたは権限が正しくありません。';
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      }

      setErrorText(errorMessage);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    // 未認証時の退避は useAuthRedirect が行う（#3203）。ここは権限による振り分けのみ扱う
    if (user === null) return;
    if (!isNotCompanyMember(user.role)) navigate('/staff');
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (!company) return;

    const userLimit = company.user_limit ?? 0;
    const currentCount = company.current_user_count ?? 0;
    if (currentCount >= userLimit) {
      navigate('/staff');
    }
  }, [company]);

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          },
        ]}
      >
        <Box sx={styles.formCard}>
          <Typography sx={styles.title}>担当者招待</Typography>
          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.email)}
                formMb="24px"
                helperText={errors.email?.message}
                labelText="メールアドレス"
                placeholder={EMAIL_INPUT_PLACEHOLDER}
                requiredText="必須"
                width="400px"
              />
            )}
            control={control}
            name="email"
          />

          <Controller
            render={({ field }): React.ReactElement => (
              <CustomRadioButton
                {...field}
                error={Boolean(errors.role)}
                helperText={errors.role?.message}
                labelText="権限"
                required
                requiredText="必須"
                trueOption="管理者"
                falseOption="メンバー"
                trueOptionDescription="担当者の閲覧・招待・編集ができます。"
                falseOptionDescription="担当者の閲覧ができます。"
              />
            )}
            control={control}
            name="role"
          />

          <Button
            disabled={buttonDisabledFlag()}
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => {
                onSubmit();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            招待メールを送る
          </Button>
        </Box>
        <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
      </Box>
    </Box>
  );
};

export default StaffInvite;
